import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import {
  createWarehouseOperator,
  fetchOperatorAssignments,
  updateWarehouseOperator,
  fetchOperatorRates,
  fetchWorkShifts,
  searchWarehouseUsers,
} from '../../../services/workforceServices'
import {
  buildOperatorPayload,
  dateFieldFor,
  extractBackendError,
  formatIsoDate,
  invalidateWorkforce,
  operatorErrorMessage,
} from '../../../utils/workforceUtils'

const CONTRACT_TYPES = ['IPS', 'MP', 'ADECCO', 'CUBBO']

const todayIso = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const useDebounced = (value, delay) => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return v
}

export const AssignOperatorSlidePanel = ({ warehouse_id, operator, onClose }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const editing = !!operator

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(editing ? operator.user_name || operator.user?.full_name || operator.full_name || '' : '')
  const debouncedQuery = useDebounced(query, 300)
  const [selectedUser, setSelectedUser] = useState(
    editing
      ? {
          id: operator.user_id ?? operator.user?.id,
          full_name: operator.user_name || operator.user?.full_name || operator.full_name,
          email: operator.user_email || operator.user?.email,
        }
      : null,
  )
  const [operatorType, setOperatorType] = useState(operator?.operator_type || '')
  const [contractType, setContractType] = useState(operator?.contract_type || '')
  const [workShiftId, setWorkShiftId] = useState(operator?.work_shift_id || operator?.work_shift?.id || '')
  const [isActive, setIsActive] = useState(operator?.operator_active ?? true)
  const { data: assignmentsData } = useQuery(
    ['workforce', warehouse_id, 'operator-assignments', operator?.id],
    () => fetchOperatorAssignments(warehouse_id, operator.id),
    { enabled: editing, retry: false, refetchOnWindowFocus: false },
  )
  const assignments = Array.isArray(assignmentsData) ? assignmentsData : []
  const today = useMemo(() => todayIso(), [])
  const dateField = dateFieldFor({ editing, operator, form: { operatorType, contractType, workShiftId, isActive }, today, history: assignmentsData })
  const [date, setDate] = useState(dateField.defaultDate || '')
  const [dateFor, setDateFor] = useState(dateField.field)
  // A new kind of date (start, effective, end) starts from its own default, not the last one typed.
  if (dateFor !== dateField.field) {
    setDateFor(dateField.field)
    setDate(dateField.defaultDate || '')
  }
  const [fieldErrors, setFieldErrors] = useState({})
  const [baseError, setBaseError] = useState('')

  useEffect(() => {
    setOpen(true)
  }, [])

  const close = (didChange = false) => {
    setOpen(false)
    setTimeout(() => onClose(didChange), 300)
  }

  const { data: ratesData } = useQuery(['workforce', warehouse_id, 'rates', {}], () => fetchOperatorRates(warehouse_id, {}), {
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })
  const operatorTypes = useMemo(() => {
    const raw = Array.isArray(ratesData) ? ratesData : ratesData?.operator_rates || ratesData?.rates || []
    return [...new Set(raw.map(r => r.operator_type).filter(Boolean))].sort()
  }, [ratesData])

  const { data: shiftsData } = useQuery(['workforce', warehouse_id, 'shifts', { include_inactive: false }], () => fetchWorkShifts(warehouse_id), {
    refetchOnWindowFocus: false,
  })
  const shifts = useMemo(() => (Array.isArray(shiftsData) ? shiftsData : shiftsData?.work_shifts || shiftsData?.shifts || []), [shiftsData])

  const { data: searchData, isFetching: isSearching } = useQuery(
    ['warehouse-users-search', warehouse_id, debouncedQuery],
    () => searchWarehouseUsers(warehouse_id, { full_name_or_email: debouncedQuery, page: 1, per_page: 20, is_enabled: true }),
    { enabled: !editing && debouncedQuery.length >= 2, keepPreviousData: true },
  )
  const searchResults = useMemo(() => {
    const raw = Array.isArray(searchData) ? searchData : searchData?.users || searchData?.operators || searchData?.data || []
    return raw
  }, [searchData])

  const mutation = useMutation(data => (editing ? updateWarehouseOperator(warehouse_id, operator.id, data) : createWarehouseOperator(warehouse_id, data)), {
    onSuccess: () => {
      invalidateWorkforce(queryClient, warehouse_id, ['operators', 'attendance', 'operator-assignments'])
      close(true)
    },
    onError: err => {
      const { fields, base } = extractBackendError(err)
      setFieldErrors(fields)
      if (base.length) setBaseError(base.join(' · '))
      else if (!Object.keys(fields).length) setBaseError(operatorErrorMessage(err, t))
      else setBaseError('')
    },
  })

  const handleSubmit = e => {
    e.preventDefault()
    setBaseError('')
    setFieldErrors({})
    const nextErrors = {}
    if (!editing && !selectedUser) nextErrors.user = t('workforce.operators.errors.user_required')
    if (!operatorType) nextErrors.operator_type = t('workforce.operators.errors.operator_type_required')
    if (!contractType) nextErrors.contract_type = t('workforce.operators.errors.contract_type_required')
    if (!workShiftId) nextErrors.work_shift = t('workforce.operators.errors.shift_required')
    if (dateField.field && !date) nextErrors.date = t(`workforce.operators.${dateField.field}`)
    else if (dateField.field && date > today) nextErrors.date = t('workforce.operators.errors.codes.FUTURE_DATE')
    else if (dateField.field && date > dateField.max) nextErrors.date = t('workforce.operators.start_date_hint')
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors)
      return
    }

    mutation.mutate(
      buildOperatorPayload({
        editing,
        operator,
        form: { operatorType, contractType, workShiftId, isActive, date },
        userId: selectedUser?.id,
        history: assignmentsData,
      }),
    )
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-20 overflow-hidden" open={open} onClose={() => close(false)}>
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-700 bg-opacity-40" />
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-lg">
                <form onSubmit={handleSubmit} className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-6 py-4 border-b flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium">
                      {editing ? t('workforce.operators.edit_title') : t('workforce.operators.assign_title')}
                    </Dialog.Title>
                    <button type="button" onClick={() => close(false)} className="text-gray-400 hover:text-gray-500">
                      <XIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    {baseError && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{baseError}</div>}

                    {editing ? (
                      <div className="p-3 bg-gray-50 rounded border text-sm">
                        <div className="font-medium">{selectedUser?.full_name}</div>
                        <div className="text-gray-500">{selectedUser?.email}</div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.operators.search_user')}</label>
                        <input
                          type="text"
                          value={query}
                          onChange={e => {
                            setQuery(e.target.value)
                            setSelectedUser(null)
                          }}
                          placeholder={t('workforce.operators.search_placeholder')}
                          className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                        />
                        {fieldErrors.user && <p className="mt-1 text-xs text-red-600">{fieldErrors.user}</p>}
                        {selectedUser ? (
                          <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm flex items-center justify-between">
                            <span>
                              <strong>{selectedUser.full_name}</strong> <span className="text-gray-500">({selectedUser.email})</span>
                            </span>
                            <button type="button" onClick={() => setSelectedUser(null)} className="text-xs text-gray-500 hover:text-gray-700">
                              {t('workforce.operators.clear')}
                            </button>
                          </div>
                        ) : (
                          debouncedQuery.length >= 2 && (
                            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md divide-y">
                              {isSearching && <div className="p-2 text-xs text-gray-500">{t('workforce.operators.searching')}</div>}
                              {!isSearching && searchResults.length === 0 && (
                                <div className="p-2 text-xs text-gray-500">{t('workforce.operators.no_results')}</div>
                              )}
                              {!isSearching &&
                                searchResults.map(u => (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedUser(u)
                                      setQuery(u.full_name || u.email || '')
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                  >
                                    <div className="font-medium">{u.full_name || u.name}</div>
                                    <div className="text-xs text-gray-500">{u.email}</div>
                                  </button>
                                ))}
                            </div>
                          )
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.operators.operator_type')}</label>
                      <select
                        value={operatorType}
                        onChange={e => setOperatorType(e.target.value)}
                        disabled={dateField.fieldsLocked}
                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                      >
                        <option value="">{t('workforce.operators.select_type')}</option>
                        {operatorTypes.map(ot => (
                          <option key={ot} value={ot}>
                            {ot}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.operator_type && <p className="mt-1 text-xs text-red-600">{fieldErrors.operator_type}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.operators.contract_type')}</label>
                      <select
                        value={contractType}
                        onChange={e => setContractType(e.target.value)}
                        disabled={dateField.fieldsLocked}
                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                      >
                        <option value="">{t('workforce.operators.select_contract')}</option>
                        {CONTRACT_TYPES.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.contract_type && <p className="mt-1 text-xs text-red-600">{fieldErrors.contract_type}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.operators.work_shift')}</label>
                      <select
                        value={workShiftId}
                        onChange={e => setWorkShiftId(e.target.value)}
                        disabled={dateField.fieldsLocked}
                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
                      >
                        <option value="">{t('workforce.operators.select_shift')}</option>
                        {shifts.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      {fieldErrors.work_shift && <p className="mt-1 text-xs text-red-600">{fieldErrors.work_shift}</p>}
                    </div>

                    {editing && (
                      <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                        <span className="text-sm text-gray-700">{t('workforce.operators.is_active')}</span>
                      </label>
                    )}

                    {dateField.field && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t(`workforce.operators.${dateField.field}`)}</label>
                        <input
                          type="date"
                          value={date}
                          max={dateField.max}
                          min={dateField.min || undefined}
                          onChange={e => setDate(e.target.value)}
                          className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                        />
                        {editing && dateField.field === 'start_date' && (
                          <p className="mt-1 text-xs text-gray-500">{t('workforce.operators.start_date_hint')}</p>
                        )}
                        {fieldErrors.date && <p className="mt-1 text-xs text-red-600">{fieldErrors.date}</p>}
                      </div>
                    )}

                    {editing && assignments.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-1">{t('workforce.operators.history')}</h3>
                        <ul className="border rounded-md divide-y text-sm">
                          {assignments.map(a => (
                            <li key={a.id} className="px-3 py-2">
                              <div className="font-medium">
                                {formatIsoDate(a.start_date)} → {a.end_date ? formatIsoDate(a.end_date) : t('workforce.operators.history_current')}
                              </div>
                              <div className="text-xs text-gray-500">{[a.work_shift_name, a.operator_type, a.contract_type].filter(Boolean).join(' · ')}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => close(false)}
                      disabled={mutation.isLoading}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      {t('workforce.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={mutation.isLoading}
                      className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {mutation.isLoading ? t('workforce.saving') : t('workforce.save')}
                    </button>
                  </div>
                </form>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
